import React, {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Formik as FormikBase, type FormikHelpers } from "formik";
import sortBy from "lodash/sortBy";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid as GridBase,
  Icon,
  Paper,
  Typography,
} from "@mui/material";

import RpSvgIcon from "@/components/icons/RpSvgIcon";
import RpCurrencyField from "@/components/muiCustom/textField/RpCurrencyField";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import BulkLocationCheckbox from "@/features/campaigns/components/bulkSelection/LocationCheckbox";
import JoinCampaignTopConfirm from "@/features/campaigns/components/joinCampaignCommon/JoinCampaignTopConfirm";
import PreviewOnlyDialog from "@/features/campaigns/components/joinCampaignCommon/PreviewOnlyDialog";
import LocationsExpandCollapseButtonsBase from "@/features/campaigns/components/LocationsExpandCollapseButtons";
import TabPanel from "@/components/muiCustom/tabs/TabPanel";
import {
  RpTab,
  RpTabs as RpTabsBase,
} from "@/components/muiCustom/tabs/JoinCampaignTab";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import useLocationBulkSelection from "@/helpers/hooks/useLocationBulkSelection";
import {
  NATIONAL_PLUS_UP_LOCATION_OPTION_ERROR,
  fetchNationalPlusUpJoinableLocations,
  formatNationalPlusUpSelectedLocations,
  getNationalPlusUpLocationsTotal,
  getNationalPlusUpNotEligibleLocations,
} from "../utils/nationalPlusUpHelpers";
import NationalPlusUpLocationAccordion from "./NationalPlusUpLocationAccordion";
import NationalPlusUpNotEligibleLocations from "./NationalPlusUpNotEligibleLocations";
import NationalPlusUpBulkSelection from "./bulk-selection/NationalPlusUpBulkSelection";

type NationalPlusUpRecord = Record<string, any>;

type NationalPlusUpFormValues = NationalPlusUpRecord & {
  is_draft?: boolean;
};

type NationalPlusUpSubmitPayload = {
  selections: NationalPlusUpRecord[];
  isDraft?: boolean;
  removeOrderFromDraftOrderIds: string[];
};

type NationalPlusUpJoinCampaignFormProps = {
  campaign?: NationalPlusUpRecord;
  initialValues?: NationalPlusUpRecord;
  onSubmit?: (_payload: NationalPlusUpSubmitPayload) => void;
  draftOrderIds?: string[];
};

const Formik = FormikBase as React.ComponentType<Record<string, any>>;
const Grid = GridBase as React.ComponentType<Record<string, any>>;
const LocationsExpandCollapseButtons =
  LocationsExpandCollapseButtonsBase as React.ComponentType<
    Record<string, any>
  >;
const RpTabs = RpTabsBase as React.ComponentType<Record<string, any>>;

function NationalPlusUpJoinCampaignForm({
  campaign,
  initialValues,
  onSubmit,
  draftOrderIds = [],
}: NationalPlusUpJoinCampaignFormProps) {
  const isPreview = !useIsParticipant();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expanded, setExpanded] = useState<Array<string | number>>([]);
  const [search, setSearch] = useState("");

  const eligibleLocations = useMemo(() => {
    const locations = campaign?.eligible_location || [];
    return sortBy(locations, "id");
  }, [campaign]);
  const campaignName = campaign?.campaign_name || "National Plus Up";

  const filteredEligibleLocations = useMemo(() => {
    if (!search) return eligibleLocations;
    return eligibleLocations.filter((location: NationalPlusUpRecord) => {
      const name = [
        location?.location_name,
        location?.market_name,
        location?.sys_location_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return name.includes(search.toLowerCase());
    });
  }, [eligibleLocations, search]);

  const notEligibleLocations = useMemo(
    () => getNationalPlusUpNotEligibleLocations(campaign),
    [campaign]
  );

  useEffect(() => {
    if (eligibleLocations.length === 0 && notEligibleLocations.length > 0) {
      setSelectedTab(1);
    }
  }, [eligibleLocations.length, notEligibleLocations.length]);

  const allLocationIds = useMemo(
    () => eligibleLocations.map((location) => location.id),
    [eligibleLocations]
  );

  useEffect(() => {
    if (allLocationIds.length === 0) {
      setExpanded([]);
      return;
    }

    if (allLocationIds.length <= 5) {
      setExpanded([...allLocationIds]);
    } else {
      setExpanded([allLocationIds[0]]);
    }
  }, [allLocationIds]);

  const {
    showLocationCheckBox,
    checkedLocations,
    disableLocations,
    isAllSelected,
    setCheckedLocations,
    setDisableLocations,
    handleToggleBulkSelection,
    handleToggleGlobalSelection,
  } = useLocationBulkSelection(eligibleLocations, "id");

  const handleOnToggleBulkSelection = useCallback(
    (isOpened: boolean) => {
      handleToggleBulkSelection(isOpened);
      if (isOpened) {
        setExpanded([]);
      }
    },
    [handleToggleBulkSelection, setExpanded]
  );

  const handleSubmit = (
    values: NationalPlusUpFormValues,
    actions: FormikHelpers<NationalPlusUpFormValues>
  ) => {
    if (isPreview) {
      actions.setSubmitting(false);
      setPreviewDialogOpen(true);
      return;
    }

    const selections = formatNationalPlusUpSelectedLocations(values, campaign);
    if (!values.is_draft && !selections.length) {
      actions.setStatus({ error: NATIONAL_PLUS_UP_LOCATION_OPTION_ERROR });
      actions.setSubmitting(false);
      return;
    }

    const removeOrderFromDraftOrderIds = Array.isArray(draftOrderIds)
      ? draftOrderIds.filter(
          (id) => !selections.some((selection) => selection.orderId === id)
        )
      : [];

    onSubmit?.({
      selections,
      isDraft: values.is_draft,
      removeOrderFromDraftOrderIds,
    });
    actions.setSubmitting(false);
  };

  return (
    <>
      <Formik
        initialValues={{ ...initialValues, is_draft: false }}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          submitForm,
          isSubmitting,
          status,
        }) => {
          const selectedLocations =
            fetchNationalPlusUpJoinableLocations(values);
          const selectedLocationsCount = selectedLocations.length;
          const totalBudget =
            getNationalPlusUpLocationsTotal(selectedLocations);
          const hasSelections = selectedLocationsCount > 0;

          const handleSaveDraft = async () => {
            await setFieldValue("is_draft", true);
            submitForm();
          };

          const handleConfirm = async () => {
            await setFieldValue("is_draft", false);
            submitForm();
          };

          return (
            <form onSubmit={handleSubmit} className="pt-3">
              <JoinCampaignTopConfirm backLinkUrl="/launch-direct-mail">
                {selectedTab === 0 ? (
                  <Grid container spacing={2} justifyContent="flex-end">
                    {!isPreview ? (
                      <Grid size={{ xs: 12, sm: "auto" }}>
                        <Button
                          type="button"
                          variant="outlined"
                          color="primary"
                          fullWidth
                          disabled={isSubmitting || !hasSelections}
                          onClick={handleSaveDraft}
                        >
                          Save for later
                        </Button>
                      </Grid>
                    ) : null}
                    <Grid size={{ xs: 12, sm: "auto" }}>
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={isSubmitting || !hasSelections}
                        onClick={handleConfirm}
                      >
                        Confirm Selections
                      </Button>
                    </Grid>
                  </Grid>
                ) : null}
              </JoinCampaignTopConfirm>

              <Box className="join-campaign-form mx-lg-5 mx-3" sx={{ pt: 2 }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: "grow" }}>
                    <Typography component="h2" variant="h5" fontWeight={700}>
                      <Icon
                        component={RpSvgIcon}
                        name="campaign"
                        color="primary"
                        className="mr-2"
                      />
                      {campaignName}
                    </Typography>
                    <Typography variant="caption">
                      Make selections to customize advertising for each of your
                      locations below.
                    </Typography>
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: "auto" }}
                    sx={{ textAlign: "right" }}
                  >
                    <RpTabs
                      value={selectedTab}
                      onChange={(_event: SyntheticEvent, newValue: number) =>
                        setSelectedTab(newValue)
                      }
                      variant="scrollable"
                      scrollButtons="off"
                    >
                      <RpTab
                        label={`Eligible Locations (${eligibleLocations.length})`}
                      />
                      <RpTab
                        label={`Not Eligible Locations (${notEligibleLocations.length})`}
                      />
                    </RpTabs>
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: "auto" }}
                    sx={{ textAlign: "right" }}
                  >
                    <LocationsExpandCollapseButtons
                      setExpanded={setExpanded}
                      expanded={expanded}
                      allLocationsIds={allLocationIds}
                      disabled={selectedTab === 1}
                    />
                  </Grid>
                </Grid>

                {selectedTab === 0 ? (
                  <Alert
                    severity="info"
                    variant="outlined"
                    icon={false}
                    sx={{
                      mb: 2,
                      bgcolor: "var(--primary-tint)",
                      borderColor: "primary.main",
                      color: "text.primary",
                      py: 1,
                      "& .MuiAlert-message": {
                        py: 0,
                      },
                    }}
                  >
                    <strong>How it works:</strong> National creative is
                    pre-selected by the brand and cannot be modified. For each
                    location, choose whether to add Plus-Up distribution and/or
                    additional store copies. The national campaign already
                    includes 2,500 store copies per location. To participate,
                    each location must select at least one option.
                  </Alert>
                ) : null}

                {status?.error ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {status.error}
                  </Alert>
                ) : null}

                <TabPanel value={selectedTab} index={0}>
                  {eligibleLocations.length > 0 ? (
                    <>
                      {eligibleLocations.length > 1 ? (
                        <NationalPlusUpBulkSelection
                          onToggleBulkSelection={handleOnToggleBulkSelection}
                          checkedLocations={checkedLocations}
                          locations={eligibleLocations}
                          setDisableLocations={setDisableLocations}
                        />
                      ) : null}

                      <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{ my: 2 }}
                      >
                        {showLocationCheckBox && (
                          <Grid item>
                            <Paper>
                              <Checkbox
                                color="primary"
                                checked={isAllSelected}
                                onChange={(event) =>
                                  handleToggleGlobalSelection(
                                    event.target.checked
                                  )
                                }
                                className="m-0 p-1"
                              />
                            </Paper>
                          </Grid>
                        )}

                        <Grid size={{ xs: 10, md: 6 }}>
                          <MuiSearchInput value={search} onChange={setSearch} />
                        </Grid>
                      </Grid>

                      {filteredEligibleLocations.length > 0 ? (
                        filteredEligibleLocations.map((location) => (
                          <Box
                            key={location.id}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            {showLocationCheckBox && (
                              <BulkLocationCheckbox
                                locationId={location.id}
                                isChecked={checkedLocations.includes(
                                  location.id
                                )}
                                setCheckedLocations={setCheckedLocations}
                                disabled={disableLocations.includes(
                                  location.id
                                )}
                              />
                            )}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <NationalPlusUpLocationAccordion
                                loc={location}
                                value={values?.[location.id]}
                                shouldExpandIt={expanded.includes(location.id)}
                                setExpanded={setExpanded}
                                setFieldValue={setFieldValue}
                              />
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box sx={{ my: 5, textAlign: "center" }}>
                          <Alert severity="info">
                            <strong>No location found.</strong>
                          </Alert>
                        </Box>
                      )}
                      {!hasSelections ? (
                        <Typography variant="caption" color="text.secondary">
                          Select Plus-Up Distribution or Store Copy for at least
                          one location to continue.
                        </Typography>
                      ) : null}
                      <NationalPlusUpTotalBudget total={totalBudget} />
                      <NationalPlusUpSubmitActions
                        selectedLocationsCount={selectedLocationsCount}
                        totalLocationsCount={eligibleLocations.length}
                        isSubmitting={isSubmitting}
                        hasSelections={hasSelections}
                        isPreview={isPreview}
                        onSaveDraft={handleSaveDraft}
                        onConfirm={handleConfirm}
                      />
                    </>
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        mt: 20,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Alert severity="info">
                        <strong>There are no Eligible Locations</strong>
                      </Alert>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel value={selectedTab} index={1}>
                  <NationalPlusUpNotEligibleLocations
                    locations={notEligibleLocations}
                  />
                </TabPanel>
              </Box>
            </form>
          );
        }}
      </Formik>
      <PreviewOnlyDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
      />
    </>
  );
}

export default NationalPlusUpJoinCampaignForm;

function NationalPlusUpTotalBudget({ total }: { total: number }) {
  return (
    <Card sx={{ mt: 7 }}>
      <CardContent>
        <Grid
          container
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Typography variant="body2" fontWeight={700}>
              Your Total Campaign Budget
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <RpCurrencyField
              value={total}
              readOnly
              placeholder="0.00"
              fixedDecimalScale={true}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function NationalPlusUpSubmitActions({
  selectedLocationsCount,
  totalLocationsCount,
  isSubmitting,
  hasSelections,
  isPreview = false,
  onSaveDraft,
  onConfirm,
}: {
  selectedLocationsCount: number;
  totalLocationsCount: number;
  isSubmitting?: boolean;
  hasSelections?: boolean;
  isPreview?: boolean;
  onSaveDraft: () => void;
  onConfirm: () => void;
}) {
  return (
    <Grid
      container
      spacing={2}
      justifyContent={{
        xs: "center",
        md: "flex-end",
      }}
      alignItems="center"
      sx={{ mt: 10 }}
    >
      <Grid size={{ xs: 12, md: "auto" }}>
        <Typography variant="body2">
          Selection made for {selectedLocationsCount} of {totalLocationsCount}{" "}
          locations
        </Typography>
      </Grid>
      {!isPreview ? (
        <Grid size={{ xs: 12, sm: 6, md: "auto" }} textAlign="center">
          <Button
            type="button"
            variant="outlined"
            color="primary"
            sx={{ px: 5 }}
            fullWidth
            disabled={isSubmitting || !hasSelections}
            onClick={onSaveDraft}
          >
            Save as Draft
          </Button>
        </Grid>
      ) : null}
      <Grid size={{ xs: 12, sm: 6, md: "auto" }} textAlign="center">
        <Button
          type="button"
          variant="contained"
          color="primary"
          sx={{ px: 7 }}
          fullWidth
          disabled={isSubmitting || !hasSelections}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </Grid>
    </Grid>
  );
}
